import axios from "axios";

// The public sign-up page (Frontend/SelfRegistration, /join/:companyCode):
// the /api/v1/self-registration routes of backend
// docs/customer-module/kyc/17-Web-Self-Registration-on-the-Journey.md §3.
//
// No staff login. Every call is marked `publicRequest`, so the interceptors in
// src/main.js add no staff Authorization header, show no toast and never log a
// staff user out. After verify, every call sends the sign-up token in the
// header X-Registration-Token, and every answer (an error too) may carry a
// renewed token in the same header, which replaces the one held. The token
// lives here only, in memory: this module is not persisted (src/store/index.js
// persists auth, companysettings and companycolors only).
//
// Session ends:
//   401 after verify  the token expired: the session is cleared and `expired`
//                     set; the page goes back to the phone step
//   409 on a write    by data.reason: "taken_over" (staff took the draft over)
//                     or "submitted" (submitted meanwhile) set `locked`, and the
//                     page shows the read-only screen of each; "state" (the
//                     draft closed, e.g. expired) or "no_application" end the
//                     session as a 401 does, and verifying again starts afresh.
//                     Any other 409 ("exists", a duplicate) stays with the caller.
//
// Actions resolve to the envelope (`res.data`) and reject with what the
// interceptor rejects with: the axios response (or undefined offline).

const BASE = "/api/v1/self-registration";
const TOKEN_HEADER = "X-Registration-Token";

function emptyState() {
    return {
        company: null,
        token: "",
        expiresAt: null,
        application: null,
        phone: "",
        expired: false,
        locked: "",
    };
}

function requestConfig(state, extra = {}) {
    const headers = { ...(extra.headers || {}) };
    if (state.token) headers[TOKEN_HEADER] = state.token;
    return { ...extra, headers, publicRequest: true };
}

// the renewed token of an answer, if it carries one
function renewedToken(response) {
    const headers = response?.headers || {};
    return headers[TOKEN_HEADER.toLowerCase()] || headers[TOKEN_HEADER] || "";
}

function isWrite(method) {
    return ["post", "put", "patch", "delete"].includes(String(method || "").toLowerCase());
}

// One call: the token in, the renewed token out, and the session ends.
function send({ state, commit }, method, url, data, extra = {}) {
    const config = requestConfig(state, extra);
    const request = ["get", "delete"].includes(method)
        ? axios[method](url, { ...config, params: data })
        : axios[method](url, data, config);

    return request
        .then((res) => {
            const token = renewedToken(res);
            if (token && state.token) commit("RENEW_TOKEN", token);
            return res.data;
        })
        .catch((err) => {
            const token = renewedToken(err);
            if (token && state.token) commit("RENEW_TOKEN", token);
            const status = Number(err?.status || err?.data?.status_code || 0);
            if (status === 401 && state.token) {
                commit("END_SESSION", { expired: true });
            } else if (status === 409 && isWrite(method) && state.token) {
                const reason = err?.data?.data?.reason;
                if (reason === "taken_over" || reason === "submitted") {
                    commit("SET_LOCKED", reason);
                } else if (reason === "state" || reason === "no_application") {
                    commit("END_SESSION", { expired: true });
                }
            }
            return Promise.reject(err);
        });
}

function isApplication(data) {
    return !!data && typeof data === "object" && !Array.isArray(data) && "customer_application_id" in data;
}

export default {
    namespaced: true,

    state: emptyState(),

    getters: {
        company: (state) => state.company,
        application: (state) => state.application,
        // the verified phone, as typed
        phone: (state) => state.phone,
        hasSession: (state) => !!state.token,
        expired: (state) => state.expired,
        // "" while the applicant may edit; "submitted" or "taken_over" once not
        locked: (state) => state.locked,
    },

    mutations: {
        SET_COMPANY(state, company) {
            state.company = company;
        },
        START_SESSION(state, { token, expiresAt, phone }) {
            state.token = token || "";
            state.expiresAt = expiresAt || null;
            state.phone = phone || "";
            state.expired = false;
            state.locked = "";
        },
        RENEW_TOKEN(state, token) {
            state.token = token;
        },
        SET_APPLICATION(state, application) {
            state.application = application || null;
        },
        SET_LOCKED(state, reason) {
            state.locked = reason;
        },
        // the token is dropped; the company stays (the same link)
        END_SESSION(state, { expired = false } = {}) {
            state.token = "";
            state.expiresAt = null;
            state.expired = expired;
        },
        CLEAR_FLAGS(state) {
            state.expired = false;
            state.locked = "";
        },
    },

    actions: {
        // GET /companies/{company_code}: {company_id, company_name, company_code}; 404 when not open
        async fetchCompany(context, companyCode) {
            const envelope = await send(context, "get", `${BASE}/companies/${encodeURIComponent(companyCode)}`);
            context.commit("SET_COMPANY", envelope?.data || null);
            return envelope;
        },

        // GET /lookups/{name}: the rows, as the staff lookups give them
        async fetchLookup(context, { name, params = {} }) {
            const query = { company_id: context.state.company?.company_id, ...params };
            Object.keys(query).forEach((key) => {
                if (query[key] === null || query[key] === undefined || query[key] === "") delete query[key];
            });
            const envelope = await send(context, "get", `${BASE}/lookups/${name}`, query);
            return Array.isArray(envelope?.data) ? envelope.data : [];
        },

        // POST /code: {resend_available_at}, the same for every phone
        async requestCode(context, { phone, phone_country }) {
            return await send(context, "post", `${BASE}/code`, {
                company_id: context.state.company?.company_id,
                phone,
                phone_country,
            });
        },

        // POST /verify: {token, expires_at, application}; application is the
        // open draft, a submitted one's {…, submitted: true}, one staff took
        // over {…, taken_over: true}, or null
        async verify(context, { phone, phone_country, code }) {
            const envelope = await send(context, "post", `${BASE}/verify`, {
                company_id: context.state.company?.company_id,
                phone,
                phone_country,
                code,
            });
            const data = envelope?.data || {};
            context.commit("START_SESSION", { token: data.token,expiresAt: data.expires_at, phone });
            context.commit("SET_APPLICATION", data.application || null);
            return envelope;
        },

        async fetchApplication(context) {
            const envelope = await send(context, "get", `${BASE}/application`);
            if (isApplication(envelope?.data)) context.commit("SET_APPLICATION", envelope.data);
            return envelope;
        },

        // individuals only: web self-registration has no groups. A 409
        // (reason "exists") is a draft that exists already (one per phone and company).
        async createApplication(context) {
            const envelope = await send(
                context,
                "post",
                `${BASE}/application`,
                { member_type: "individual" }
            );
            if (isApplication(envelope?.data)) context.commit("SET_APPLICATION", envelope.data);
            return envelope;
        },

        // PATCH /application: {section, ...fields}. useApplicationSave passes
        // the id too: dropped, the application is always the token's own.
        async saveSection(context, payload) {
            const body = { ...payload };
            delete body.id;
            const envelope = await send(context, "patch", `${BASE}/application`, body);
            if (isApplication(envelope?.data)) context.commit("SET_APPLICATION", envelope.data);
            return envelope;
        },

        // PUT /application/contacts: {next_of_kin, referees}
        async saveContacts(context, { next_of_kin, referees }) {
            return await send(context, "put", `${BASE}/application/contacts`, { next_of_kin, referees });
        },

        // GET /application/documents: [DocumentEntry], as the staff list
        async fetchDocuments(context) {
            return await send(context, "get", `${BASE}/application/documents`);
        },

        // POST /application/files: one file, uploaded at once
        async uploadFile(context, { documentTypeId, file, idempotencyKey, onProgress }) {
            const form = new FormData();
            form.append("file", file);
            form.append("document_type_id", documentTypeId);

            const extra = { showLoader: false, headers: {} };
            if (idempotencyKey) extra.headers["Idempotency-Key"] = idempotencyKey;
            if (typeof onProgress === "function") {
                extra.onUploadProgress = (event) => {
                    if (event && event.total) onProgress(Math.round((event.loaded * 100) / event.total));
                };
            }
            return await send(context, "post", `${BASE}/application/files`, form, extra);
        },

        // DELETE /application/files/{file_id}: an upload not saved on a document
        async discardFile(context, fileId) {
            return await send(context, "delete", `${BASE}/application/files/${encodeURIComponent(fileId)}`);
        },

        // PUT /application/documents/{document_type_id}: {document_number?, issued_at?, expires_at?, file_ids}
        async saveDocument(context, { documentTypeId, payload }) {
            return await send(context, "put", `${BASE}/application/documents/${documentTypeId}`, payload);
        },

        async removeDocument(context, documentTypeId) {
            return await send(context, "delete", `${BASE}/application/documents/${documentTypeId}`);
        },

        // POST /application/submit: {reference, amount, status_id}; the session ends
        async submit(context, { password, password_confirmation, declaration_accepted }) {
            const envelope = await send(context, "post", `${BASE}/application/submit`, {
                password,
                password_confirmation,
                declaration_accepted,
            });
            context.commit("END_SESSION");
            return envelope;
        },

        // a new start on the same link (the phone step)
        reset({ commit }) {
            commit("END_SESSION");
            commit("SET_APPLICATION", null);
            commit("CLEAR_FLAGS");
        },
    },
};
